"use client"

import { Pack } from "@assets"
import { ImageIcon, LinkIcon, Percent, ShoppingBasket } from "lucide-react"
import type { FormEvent } from "react"
import { useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

export interface Category {
  id: number
  name: string
}

export interface ProductFormData {
  name: string
  description: string
  price: string
  sale_price?: string
  currency: string
  in_stock: boolean
  stock_quantity?: string
  unit?: string
  category_id?: string
  business_id?: string
  status: "active" | "inactive" | "out_of_stock"
  condition?: "new" | "like_new" | "good" | "fair"
  tags?: string
  images?: File[]
  image_urls?: string[]
  images_to_delete?: (number | string)[]
}

export interface ProductFormProps {
  title: string
  data: ProductFormData
  errors: Record<string, string>
  categories: Category[]
  businesses?: { id: number; business_name: string }[]
  processing?: boolean
  existingImages?: { id: number | string; url: string }[]
  onChange: (partial: Partial<ProductFormData>) => void
  onSubmit: (e: FormEvent) => void
  onCancel: () => void
  submitLabel?: string
}

const conditions = [
  { value: "new", label: "Nuevo" },
  { value: "like_new", label: "Como nuevo" },
  { value: "good", label: "Buen estado" },
  { value: "fair", label: "Estado aceptable" },
]

const statuses = [
  { value: "active", label: "Activo" },
  { value: "inactive", label: "Inactivo" },
  { value: "out_of_stock", label: "Sin stock" },
]

export function ProductForm({
  title,
  data,
  errors,
  categories,
  businesses = [],
  processing = false,
  existingImages = [],
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Guardar",
}: ProductFormProps) {
  const err = errors
  const [imageMode, setImageMode] = useState<"file" | "url">("file")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [newPreviews, setNewPreviews] = useState<{ url: string; file: File }[]>(
    [],
  )
  const [urlInput, setUrlInput] = useState("")
  const [imagesToDelete, setImagesToDelete] = useState<Set<number | string>>(
    new Set(),
  )

  const toggleImageForDeletion = (imageId: number | string) => {
    const newSet = new Set(imagesToDelete)
    if (newSet.has(imageId)) {
      newSet.delete(imageId)
    } else {
      newSet.add(imageId)
    }
    setImagesToDelete(newSet)
    onChange({ images_to_delete: Array.from(newSet) })
  }

  return (
    <div className="w-full max-w-4xl">
      <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
        <ShoppingBasket className="text-primary h-8 w-8" />
        {title}
      </CardTitle>
      <CardContent className="px-4">
        {Object.keys(err).length > 0 && (
          <div className="alert-error mb-6">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-status-error h-2 w-2 rounded-full"></div>
              <p className="text-destructive font-semibold">
                Por favor corrige los siguientes errores:
              </p>
            </div>
            <ul className="text-destructive list-disc space-y-1 pl-6 text-sm">
              {Object.entries(err).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {businesses.length > 0 && (
              <div className="md:col-span-2">
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Negocio
                </Label>
                <Select
                  value={data.business_id ?? ""}
                  onValueChange={(value) => onChange({ business_id: value })}
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5 transition-colors duration-200">
                    <SelectValue placeholder="Selecciona un negocio" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.business_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Nombre
              </Label>
              <Input
                value={data.name}
                onChange={(e) => onChange({ name: e.target.value })}
                required
                placeholder="Ej: Mate Imperial"
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
              />
            </div>
            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Categoría
              </Label>
              <Select
                value={data.category_id ?? ""}
                onValueChange={(value) => onChange({ category_id: value })}
              >
                <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5 transition-colors duration-200">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Descripción
              </Label>
              <Textarea
                value={data.description}
                onChange={(e) => onChange({ description: e.target.value })}
                rows={4}
                placeholder="Describe tu producto..."
                className="border-border focus:border-primary resize-none border-2 text-base transition-colors duration-200"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-secondary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Pack className="text-secondary h-5 w-5" />
                Precio y Stock
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Precio
                </Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={data.price}
                  onChange={(e) => onChange({ price: e.target.value })}
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                />
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Precio oferta (opcional)
                </Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={data.sale_price ?? ""}
                  onChange={(e) => onChange({ sale_price: e.target.value })}
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                />
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Moneda
                </Label>
                <Input
                  value={data.currency}
                  onChange={(e) => onChange({ currency: e.target.value })}
                  placeholder="EUR"
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                />
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  En stock
                </Label>
                <Select
                  value={data.in_stock ? "true" : "false"}
                  onValueChange={(v) => onChange({ in_stock: v === "true" })}
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5 transition-colors duration-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Sí</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Cantidad
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={data.stock_quantity ?? ""}
                  onChange={(e) => onChange({ stock_quantity: e.target.value })}
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                />
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Unidad
                </Label>
                <Input
                  value={data.unit ?? ""}
                  onChange={(e) => onChange({ unit: e.target.value })}
                  placeholder="kg, unidad, caja..."
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-accent h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <Percent className="text-primary h-5 w-5" />
                Estado y Condición
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Estado
                </Label>
                <Select
                  value={data.status}
                  onValueChange={(v) =>
                    onChange({ status: v as ProductFormData["status"] })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5 transition-colors duration-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Condición
                </Label>
                <Select
                  value={data.condition ?? "new"}
                  onValueChange={(v) =>
                    onChange({ condition: v as ProductFormData["condition"] })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5 transition-colors duration-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <ImageIcon className="text-primary h-5 w-5" />
                Imágenes
              </h3>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={imageMode === "file" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("file")}
              >
                <ImageIcon className="mr-2 h-4 w-4" /> Subir archivos
              </Button>
              <Button
                type="button"
                variant={imageMode === "url" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("url")}
              >
                <LinkIcon className="mr-2 h-4 w-4" /> Usar URLs
              </Button>
            </div>
            {imageMode === "file" && (
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : []
                  if (files.length === 0) return
                  const mergedFiles = [...(data.images ?? []), ...files]
                  onChange({ images: mergedFiles })
                  const previews = files.map((f) => ({
                    url: URL.createObjectURL(f),
                    file: f,
                  }))
                  setNewPreviews((prev) => [...prev, ...previews])
                  if (fileInputRef.current) fileInputRef.current.value = ""
                }}
                className="border-muted file:bg-primary/10 file:text-primary hover:file:bg-primary/20 h-12 w-full border-2 border-dashed file:mr-4 file:truncate file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium"
              />
            )}
            {imageMode === "url" && (
              <div className="mt-3 flex gap-2">
                <Input
                  type="url"
                  placeholder="Pega una URL de imagen y presiona Enter o Agregar"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      const v = urlInput.trim()
                      if (!v) return
                      const next = [...(data.image_urls ?? []), v]
                      onChange({ image_urls: next })
                      setUrlInput("")
                    }
                  }}
                  className="h-12 text-base"
                />
                <Button
                  type="button"
                  onClick={() => {
                    const v = urlInput.trim()
                    if (!v) return
                    const next = [...(data.image_urls ?? []), v]
                    onChange({ image_urls: next })
                    setUrlInput("")
                  }}
                >
                  Agregar
                </Button>
              </div>
            )}
            {(Array.isArray(existingImages) && existingImages.length > 0) ||
            newPreviews.length > 0 ||
            (data.image_urls && data.image_urls.length > 0) ? (
              <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                {existingImages.map(
                  (img: { id: number | string; url: string }) => {
                    const isMarkedForDeletion = imagesToDelete.has(img.id)
                    if (isMarkedForDeletion) return null
                    return (
                      <div
                        key={`ex-${String(img.id)}`}
                        className="border-border bg-muted hover:border-primary relative aspect-square overflow-hidden rounded-lg border-2 transition-all"
                      >
                        <img
                          src={img.url}
                          alt="Imagen existente"
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => toggleImageForDeletion(img.id)}
                          className="hover:bg-destructive absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-xs font-bold text-white"
                          title="Eliminar imagen"
                        >
                          ✕
                        </button>
                      </div>
                    )
                  },
                )}
                {newPreviews.map((p, idx) => (
                  <div
                    key={`new-${idx}`}
                    className="border-border bg-muted relative aspect-square overflow-hidden rounded-lg border-2"
                  >
                    <img
                      src={p.url}
                      alt="Nueva imagen"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      className="hover:bg-destructive absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-xs font-bold text-white"
                      title="Quitar esta imagen"
                      onClick={() => {
                        URL.revokeObjectURL(p.url)
                        setNewPreviews((prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                        const remaining = (data.images ?? []).filter(
                          (f) => f !== p.file,
                        )
                        onChange({ images: remaining })
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {(data.image_urls ?? []).map((u: string, idx: number) => (
                  <div
                    key={`url-${idx}-${u}`}
                    className="border-border bg-muted relative aspect-square overflow-hidden rounded-lg border-2"
                  >
                    <img
                      src={u}
                      alt="Imagen por URL"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      className="hover:bg-destructive absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-xs font-bold text-white"
                      title="Quitar esta URL"
                      onClick={() => {
                        const next = (data.image_urls ?? []).filter(
                          (_, i) => i !== idx,
                        )
                        onChange({ image_urls: next })
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            {/* URL input moved above the grid */}
          </div>

          <div className="border-border flex justify-end gap-4 border-t pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="h-9 bg-transparent px-4 text-sm font-medium"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="h-9 px-4 text-sm font-medium"
            >
              {processing ? "Guardando..." : submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  )
}
